import { useState } from 'react';
import { Box, Typography, Grid, TextField, Container, InputAdornment } from '@mui/material';
import { useRecoilState, useSetRecoilState } from 'recoil';

import {
  inputDataState,
  dayState,
  warehouseState,
  clientState,
  workloadState,
  pricingState,
  simulatingSelectorState,
} from './States';
import { createShipmonkSimulationRoutingProblem } from '@/repositories/HttpRepository';
import useStyles from './styles';
import LoadingButton from '@/components/Button/LoadingButton';

export default function PricingShipMonkSimulation() {
  const [loading, setLoading] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [status, setStatus] = useState({});
  const [canCreateShipments, setCanCreateShipment] = useState(false);
  const [creatingSimulation, setCreatingSimulation] = useState(false);
  const [deleting, setDeleting] = useState({});
  const [notice, setNotice] = useState({ color: '#5ed212', msg: '' });
  const [message, setMessage] = useState({ color: '#5ed212', msg: '' });

  const [inputData, setInputData] = useRecoilState(inputDataState);
  const setDay = useSetRecoilState(dayState);
  const setWarehouseId = useSetRecoilState(warehouseState);
  const setWorkload = useSetRecoilState(workloadState);
  const setClientId = useSetRecoilState(clientState);
  const setPricing = useSetRecoilState(pricingState);
  const [simulating, setSimulating] = useRecoilState(simulatingSelectorState);

  const { classes } = useStyles();

  const onChangePricing = (field) => (e) => {
    let value = parseFloat(e.target.value);
    if (!Number.isFinite(value)) {
      value = 0;
    }
    setPricing({ [field]: value });
  };

  const createSimulation = () => {
    // Validate required numeric fields
    const warehouseId = parseInt(inputData.warehouse_id, 10);
    const clientId = parseInt(inputData.client_id, 10);
    const day = parseInt(inputData.day, 10);
    const workload = parseFloat(inputData.workload);

    if (Number.isNaN(warehouseId) || Number.isNaN(clientId) || Number.isNaN(day) || Number.isNaN(workload)) {
      setMessage({ color: 'red', msg: 'Invalid numeric field: warehouse_id, client_id, day, and workload must be valid numbers' });
      setCreatingSimulation(false);
      return;
    }

    // Sanitize pricing fields
    const pricing = inputData.pricing || {};
    const sanitizedPricing = {
      minimum: Number.isNaN(parseFloat(pricing.minimum)) ? 0 : parseFloat(pricing.minimum),
      box_rate: Number.isNaN(parseFloat(pricing.box_rate)) ? 0 : parseFloat(pricing.box_rate),
      mileage_rate: Number.isNaN(parseFloat(pricing.mileage_rate)) ? 0 : parseFloat(pricing.mileage_rate),
      extra_box_rate: Number.isNaN(parseFloat(pricing.extra_box_rate)) ? 0 : parseFloat(pricing.extra_box_rate),
      mileage_credit: Number.isNaN(parseFloat(pricing.mileage_credit)) ? 0 : parseFloat(pricing.mileage_credit),
    };

    const sanitizedData = {
      ...inputData,
      warehouse_id: warehouseId,
      client_id: clientId,
      day: day,
      workload: workload,
      pricing: sanitizedPricing,
    };

    setMessage({ color: '', msg: 'Creating problem' });
    setCreatingSimulation(true);
    createShipmonkSimulationRoutingProblem(sanitizedData)
      .then((saveRes) => {
        if (saveRes.ok) {
          setMessage({ color: '', msg: 'Problem has been created!' });
        } else {
          setMessage({ color: 'red', msg: 'Failed to create problem' });
        }
      })
      .catch(() => {
        setMessage({ color: 'red', msg: 'Failed to create problem' });
      })
      .finally(() => {
        setCreatingSimulation(false);
      });
  };

  return (
    <Container>
      <Box p={3}>
        <Box pb={3}>
          <Typography variant="h3" className={classes.headerTitle}>
            Simulate Delivery Pricing
          </Typography>
        </Box>
        <Box>
          <Box pb={2} className={classes.fieldTitle}>
            Input Data
          </Box>
          <Grid container spacing={1} alignItems="center" style={{ marginTop: 10 }}>
            <Grid size={2}>
              Pricing Params
            </Grid>
            <Grid size={2}>
              <TextField
                label="Minimum Price"
                type="number"
                value={inputData.pricing.minimum}
                onChange={onChangePricing('minimum')}
                fullWidth
                disabled={simulating || creatingSimulation}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  },
                  htmlInput: { min: 0, step: 0.01 },
                }}
                variant="outlined"
                margin="dense"
                size="small"
              />
            </Grid>
            <Grid size={2}>
              <TextField
                label="Box Rate"
                type="number"
                value={inputData.pricing.box_rate}
                onChange={onChangePricing('box_rate')}
                fullWidth
                disabled={simulating || creatingSimulation}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  },
                  htmlInput: { min: 0, step: 0.01 },
                }}
                variant="outlined"
                margin="dense"
                size="small"
              />
            </Grid>
            <Grid size={2}>
              <TextField
                label="Extra Box Rate"
                type="number"
                value={inputData.pricing.extra_box_rate}
                onChange={onChangePricing('extra_box_rate')}
                fullWidth
                disabled={simulating || creatingSimulation}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  },
                  htmlInput: { min: 0, step: 0.01 },
                }}
                variant="outlined"
                margin="dense"
                size="small"
              />
            </Grid>
            <Grid size={2}>
              <TextField
                label="Mileage Rate"
                type="number"
                value={inputData.pricing.mileage_rate}
                onChange={onChangePricing('mileage_rate')}
                fullWidth
                disabled={simulating || creatingSimulation}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  },
                  htmlInput: { min: 0, step: 0.01 },
                }}
                variant="outlined"
                margin="dense"
                size="small"
              />
            </Grid>
            <Grid size={2}>
              <TextField
                label="Mileage Credit"
                type="number"
                value={inputData.pricing.mileage_credit}
                onChange={onChangePricing('mileage_credit')}
                fullWidth
                disabled={simulating || creatingSimulation}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  },
                  htmlInput: { min: 0, step: 0.01 },
                }}
                variant="outlined"
                margin="dense"
                size="small"
              />
            </Grid>

            <Grid size={2}>
              Warehouse ID
            </Grid>
            <Grid size={2}>
              <TextField
                label="Warehouse ID"
                type="number"
                value={inputData.warehouse_id}
                onChange={(event) => setWarehouseId(event.target.value || '')}
                fullWidth
                disabled={simulating || creatingSimulation}
                slotProps={{
                  htmlInput: { min: 1 },
                }}
                variant="outlined"
                margin="dense"
                size="small"
              />
            </Grid>
            <Grid size={2}>
              <TextField
                label="Client ID"
                type="number"
                value={inputData.client_id}
                onChange={(event) => setClientId(event.target.value || '')}
                fullWidth
                disabled={simulating || creatingSimulation}
                slotProps={{
                  htmlInput: { min: 1 },
                }}
                variant="outlined"
                margin="dense"
                size="small"
              />
            </Grid>
            <Grid size={2}>
              <TextField
                label="Day"
                type="number"
                value={inputData.day}
                onChange={(event) => setDay(event.target.value || '')}
                fullWidth
                disabled={simulating || creatingSimulation}
                slotProps={{
                  htmlInput: { min: 1 },
                }}
                variant="outlined"
                margin="dense"
                size="small"
              />
            </Grid>
            <Grid size={2}>
              <TextField
                label="Workload"
                type="number"
                value={inputData.workload}
                onChange={(event) => setWorkload(event.target.value || '')}
                fullWidth
                disabled={simulating || creatingSimulation}
                slotProps={{
                  htmlInput: { min: 1 },
                }}
                variant="outlined"
                margin="dense"
                size="small"
              />
            </Grid>
            <Grid size={2}>
              <LoadingButton
                color="primary"
                variant="contained"
                style={{ marginBottom: 20 }}
                onClick={createSimulation}
                disabled={creatingSimulation}
                label={'Create Simulation'}
                loading={creatingSimulation}
              />
              {message?.msg && <small style={{ color: message.color }}>{message.msg}</small>}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
}
